import _ from 'lodash';

export interface Country {
  displayName: string;
  geocodedName: string;
  locales: Locale[];
  currency: string;
}

export interface Locale {
  text: string;
  value: string;
}

// NOTE: If you add supported countries or locales, also update i18nUtils.ts with the way time should
// be formatted for any additional locales.
const supportedCountries: { [countryCode: string]: Country } = {
  AE: {
    displayName: 'United Arab Emirates (الإمارات العربية المتحدة)',
    geocodedName: 'UAE',
    locales: [
      { value: 'ar-ae', text: 'عربي' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'AED',
  },
  AL: {
    displayName: 'Albania (Shqipëria)',
    geocodedName: 'Albania',
    locales: [
      { value: 'sq-al', text: 'shqiptare' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'ALL',
  },
  AR: {
    displayName: 'Argentina',
    geocodedName: 'Argentina',
    locales: [
      { value: 'es-ar', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'ARS',
  },
  AT: {
    displayName: 'Austria (Österreich)',
    geocodedName: 'Austria',
    locales: [
      { value: 'de-at', text: 'Deutsch' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  AU: {
    displayName: 'Australia',
    geocodedName: 'Australia',
    locales: [{ value: 'en-au', text: 'English' }],
    currency: 'AUD',
  },
  BE: {
    displayName: 'Belgium (België)',
    geocodedName: 'Belgium',
    locales: [
      { value: 'nl-be', text: 'Nederlands' },
      { value: 'fr-be', text: 'français' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  BM: {
    displayName: 'Bermuda',
    geocodedName: 'Bermuda',
    locales: [{ value: 'en-bm', text: 'English' }],
    currency: 'BMD',
  },
  BG: {
    displayName: 'Bulgaria (България)',
    geocodedName: 'Bulgaria',
    locales: [
      { value: 'bg-bg', text: 'България' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'BGN',
  },
  BR: {
    displayName: 'Brazil (Brasil)',
    geocodedName: 'Brazil',
    locales: [
      { value: 'pt-br', text: 'Português' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'BRL',
  },
  BS: {
    displayName: 'The Bahamas',
    geocodedName: 'The Bahamas',
    locales: [{ value: 'en-us', text: 'English' }],
    currency: 'USD',
  },
  BZ: {
    displayName: 'Belize',
    geocodedName: 'Belize',
    locales: [{ value: 'en-us', text: 'English' }],
    currency: 'BZD',
  },
  CA: {
    displayName: 'Canada',
    geocodedName: 'Canada',
    locales: [
      { value: 'en-ca', text: 'English' },
      { value: 'fr-ca', text: 'français' },
    ],
    currency: 'CAD',
  },
  CH: {
    displayName: 'Switzerland',
    geocodedName: 'Switzerland',
    locales: [
      { value: 'fr-ch', text: 'français' },
      { value: 'de-ch', text: 'Deutsch' },
      { value: 'it-ch', text: 'italiano' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'CHF',
  },
  CL: {
    displayName: 'Chile',
    geocodedName: 'Chile',
    locales: [
      { value: 'es-cl', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'CLP',
  },
  CO: {
    displayName: 'Colombia',
    geocodedName: 'Colombia',
    locales: [
      { value: 'es-co', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'COP',
  },
  CR: {
    displayName: 'Costa Rica',
    geocodedName: 'Cosa Rica',
    locales: [
      { value: 'es-cr', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'USD', // We can only operate in Costa Rica in USD.
  },
  CZ: {
    displayName: 'Czech Republic (Česká republika)',
    geocodedName: 'Czech Republic',
    locales: [
      { value: 'cs-cz', text: 'čeština' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'CZK',
  },
  DE: {
    displayName: 'Germany (Deutschland)',
    geocodedName: 'Germany',
    locales: [
      { value: 'de-de', text: 'Deutsch' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  DR: {
    displayName: 'Dominican Republic',
    geocodedName: 'Dominican Republic',
    locales: [{ value: 'es-do', text: 'español' }],
    currency: 'DOP',
  },
  DK: {
    displayName: 'Denmark (Danmark)',
    geocodedName: 'Denmark',
    locales: [
      { value: 'da-dk', text: 'dansk' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'DKK',
  },
  ES: {
    displayName: 'Spain (España)',
    geocodedName: 'Spain',
    locales: [
      { value: 'es-es', text: 'español' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  EC: {
    displayName: 'Ecuador',
    geocodedName: 'Ecuador',
    locales: [
      { value: 'es-ec', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'USD',
  },
  GE: {
    displayName: 'Georgia (საქართველო)',
    geocodedName: 'Georgia',
    locales: [
      { value: 'ka-ge', text: 'ქართული' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'GEL',
  },
  FI: {
    displayName: 'Finland (Suomi)',
    geocodedName: 'Finland',
    locales: [
      { value: 'fi-fi', text: 'suomeksi' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  FR: {
    displayName: 'France',
    geocodedName: 'France',
    locales: [
      { value: 'fr-fr', text: 'français' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  GB: {
    displayName: 'United Kingdom',
    geocodedName: 'United Kingdom',
    locales: [{ value: 'en-gb', text: 'English' }],
    currency: 'GBP',
  },
  GG: {
    displayName: 'Guernsey',
    geocodedName: 'Guernsey',
    locales: [
      { value: 'en-gg', text: 'English' },
      { value: 'fr-fr', text: 'français' },
    ],
    currency: 'GGP',
  },
  GR: {
    displayName: 'Greece (Ελλάδα)',
    geocodedName: 'Greece',
    locales: [
      { value: 'el-gr', text: 'Ελληνικά' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  GT: {
    displayName: 'Guatemala',
    geocodedName: 'Guatemala',
    locales: [
      { value: 'es-gt', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'GTQ',
  },
  GY: {
    displayName: 'Guyana',
    geocodedName: 'Guyana',
    locales: [{ value: 'en-gy', text: 'English' }],
    currency: 'GYD',
  },
  HK: {
    displayName: 'Hong Kong (香港)',
    geocodedName: 'Hong Kong, China',
    // zh-cn since zh-sg is not yet supported and there's no default back to zh.
    locales: [
      { value: 'zh-hans-hk', text: '中文' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'HKD',
  },
  HN: {
    displayName: 'Honduras',
    geocodedName: 'Honduras',
    locales: [{ value: 'es-hn', text: 'español' }],
    currency: 'HNL',
  },
  HR: {
    displayName: 'Croatia (Hrvatska)',
    geocodedName: 'Croatia',
    locales: [
      { value: 'hr-hr', text: 'Hrvatski' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'HRK',
  },
  HU: {
    displayName: 'Hungary (Magyarország))',
    geocodedName: 'Hungary',
    locales: [
      { value: 'hu-hu', text: 'Magyar' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'HUF',
  },
  IE: {
    displayName: 'Ireland',
    geocodedName: 'Ireland',
    locales: [{ value: 'en-ie', text: 'English' }],
    currency: 'EUR',
  },
  IL: {
    displayName: 'Israel',
    geocodedName: 'Israel',
    locales: [
      { value: 'ar-il', text: `Isrā'īl` },
      { value: 'he-il', text: `Yisra'el` },
      { value: 'en-il', text: 'English' },
    ],
    currency: 'ILS',
  },
  IT: {
    displayName: 'Italy (Italia)',
    geocodedName: 'Italy',
    locales: [
      { value: 'it-it', text: 'italiano' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  IN: {
    displayName: 'India (इंडिया)',
    geocodedName: 'India',
    locales: [
      { value: 'hi-in', text: 'हिंदी' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'INR',
  },
  JP: {
    displayName: 'Japan (日本)',
    geocodedName: 'Japan',
    locales: [
      { value: 'ja-jp', text: '日本語' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'JPY',
  },
  KE: {
    displayName: 'Kenya',
    geocodedName: 'Kenya',
    locales: [
      { value: 'sw-ke', text: 'Kiswahili' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'KES',
  },
  KN: {
    displayName: 'Saint Kitts and Nevis',
    geocodedName: 'Saint Kitts and Nevis',
    locales: [{ value: 'en-us', text: 'English' }],
    currency: 'XCD',
  },
  KR: {
    displayName: 'South Korea (한국)',
    geocodedName: 'South Korea',
    locales: [
      { value: 'ko-kr', text: '한국어' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'KRW',
  },
  KY: {
    displayName: 'Cayman Islands',
    geocodedName: 'Cayman Islands',
    locales: [{ value: 'en-ky', text: 'English' }],
    currency: 'KYD',
  },
  LU: {
    displayName: 'Luxembourg',
    geocodedName: 'Luxembourg',
    locales: [
      { value: 'de-lu', text: 'Deutsch' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  LV: {
    displayName: 'Latvia (Latvija)',
    geocodedName: 'Latvia',
    locales: [
      { value: 'lv-lv', text: 'Latvietis' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  MA: {
    displayName: 'Morocco (المغرب)',
    geocodedName: 'Morocco',
    locales: [
      { value: 'ar-ma', text: 'عربى' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'USD',
  },
  MY: {
    displayName: 'Malaysia',
    geocodedName: 'Malaysia',
    locales: [
      { value: 'ms-my', text: 'Malay' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'MYR',
  },
  MX: {
    displayName: 'Mexico (Méjico)',
    geocodedName: 'Mexico',
    locales: [
      { value: 'es-mx', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'MXN',
  },
  NL: {
    displayName: 'Netherlands (Nederland)',
    geocodedName: 'Netherlands',
    locales: [
      { value: 'nl-nl', text: 'Nederlands' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  NO: {
    displayName: 'Norway (Norge)',
    geocodedName: 'Norway',
    locales: [
      { value: 'nb-no', text: 'norsk' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'NOK',
  },
  NZ: {
    displayName: 'New Zealand',
    geocodedName: 'New Zealand',
    locales: [{ value: 'en-nz', text: 'English' }],
    currency: 'NZD',
  },
  PA: {
    displayName: 'Panama',
    geocodedName: 'Panama',
    locales: [
      { value: 'es-pa', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'USD',
  },
  PE: {
    displayName: 'Perú',
    geocodedName: 'Peru',
    locales: [
      { value: 'es-pe', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'PEN',
  },
  PH: {
    displayName: 'Philippines',
    geocodedName: 'Philippines',
    locales: [{ value: 'en-us', text: 'English' }],
    currency: 'PHP',
  },
  PL: {
    displayName: 'Poland (Polska)',
    geocodedName: 'Poland',
    locales: [
      { value: 'pl-pl', text: 'polski' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'PLN',
  },
  PR: {
    displayName: 'Puerto Rico',
    geocodedName: 'Puerto Rico',
    locales: [
      { value: 'es-pr', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'USD',
  },
  PT: {
    displayName: 'Portugal',
    geocodedName: 'Portugal',
    locales: [
      { value: 'pt-pt', text: 'Português' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'EUR',
  },
  RO: {
    displayName: 'Romania (România)',
    geocodedName: 'Romania',
    locales: [
      { value: 'ro-ro', text: 'Română' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'RON',
  },
  RU: {
    displayName: 'Russia (Россия)',
    geocodedName: 'Russia',
    locales: [
      { value: 'ru-ru', text: 'русский' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'RUB',
  },
  SE: {
    displayName: 'Sweden (Sverige)',
    geocodedName: 'Sweden',
    locales: [
      { value: 'sv-se', text: 'svenska' },
      { value: 'en-gb', text: 'English' },
    ],
    currency: 'SEK',
  },
  SG: {
    displayName: 'Singapore (新加坡)',
    geocodedName: 'Singapore',
    // zh-cn since zh-sg is not yet supported and there's no default back to zh.
    locales: [
      { value: 'zh-hans-sg', text: '中文' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'SGD',
  },
  SL: {
    displayName: 'Slovenia (Slovenija)',
    geocodedName: 'Slovenia',
    locales: [
      { value: 'sl-si', text: 'slovenski' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
  SV: {
    displayName: 'El Salvador',
    geocodedName: 'El Salvador',
    locales: [
      { value: 'es-sv', text: 'español' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'SVC',
  },
  TH: {
    displayName: 'Thailand (ประเทศไทย)',
    geocodedName: 'Thailand',
    locales: [
      { value: 'th-th', text: 'ไทย' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'THB',
  },
  VN: {
    displayName: 'Vietnam (Việt Nam)',
    geocodedName: 'Vietnam',
    locales: [
      { value: 'vi-vn', text: 'Tiếng Việt' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'VND',
  },
  US: {
    displayName: 'United States',
    geocodedName: 'United States',
    locales: [{ value: 'en-us', text: 'English' }],
    currency: 'USD',
  },

  SAINT_BARTS: {
    displayName: 'Saint Barthélemy',
    geocodedName: 'Saint Barthélemy',
    locales: [
      { value: 'fr-fr', text: 'français' },
      { value: 'en-us', text: 'English' },
    ],
    currency: 'EUR',
  },
};

export const supportedCountriesOptions = _.sortBy(
  _.map(supportedCountries, (country: Country, code: string) => ({
    key: code,
    label: country.displayName,
    value: code,
  })),
  'label'
);

export const supportedCountriesFormOptions = () => {
  return _.map(supportedCountries, (country: Country, code: string) => {
    return (
      <option key={code} value={code} label={country.displayName}>
        {country.displayName}
      </option>
    );
  }).sort((a, b) => {
    const labelA = a.props.label;
    const labelB = b.props.label;
    if (labelA < labelB) {
      return -1;
    } else if (labelA > labelB) {
      return 1;
    } else {
      return 0;
    }
  });
};

export function isSupportedCountry(name: string) {
  const deburrName = _.toLower(_.deburr(name));
  return !_.isEmpty(
    _.find(supportedCountries, (country) => {
      if (
        _.startsWith(_.deburr(_.toLower(country.displayName)), deburrName) ||
        _.startsWith(_.deburr(_.toLower(country.geocodedName)), deburrName)
      ) {
        return true;
      }
      return false;
    })
  );
}

export const supportedLocales = _.uniq(
  _.map(_.flatten(_.map(_.values(supportedCountries), 'locales')), 'value')
);

export const supportedCurrencies = _.sortBy(
  _.uniq(_.map(_.values(supportedCountries), 'currency'))
);

export function getDefaultCurrencyForCountry(country: string) {
  return supportedCountries[country] && supportedCountries[country].currency;
}

export function guessCountryCode(country: string): string {
  if (!country) {
    return '';
  }
  const maybeCountryCode = _.toUpper(country);
  const countryCode = supportedCountries[maybeCountryCode];
  if (countryCode) {
    return maybeCountryCode;
  }
  const lowerCaseCountry = _.toLower(country);
  const foundByName = _.findKey(supportedCountries, (countryInfo) => {
    return _.toLower(countryInfo.geocodedName) === lowerCaseCountry;
  });
  return foundByName || '';
}

export function getCountryLocaleForCurrency(currencyCode: string) {
  return _.map(supportedCountries, (country: Country) => {
    return { currency: country.currency, locale: country.locales[0].value };
  })
    .filter((countries) => countries.currency === currencyCode)
    .map((country) => country.locale)[0];
}

export default supportedCountries;
